import DlAnimate from 'dl-animate/dist/dl-animate-module';
import { TweenLite } from 'gsap/TweenLite';
import toggleModal from '@/scripts/utils/toggle-modal';
import { DOCUMENT_ELEMENT, KEY_CODES } from '@/scripts/constants';
import lineHeight from 'line-height';
import isMobile from '@/scripts/utils/detect-mobile';
import isMicrosoft from '@/scripts/utils/detect-microsoft';
import '@/scripts/lettering-surrogate-pairs';
import HoverLetterSpacing from '@/scripts/utils/hover-letter-spacing';

const dlAnimate = new DlAnimate();
const defaults = {
  menuSelector: '[data-role="menu"]', // .menu,
  menuContainer: '[data-role="menu-list-container"]', // .menu__container inner
  menuScrollElement: '[data-role="menu-scroll-element"]', // .menu__container inner
  menuWrapper: '[data-role="menu-wrapper"]', // .menu__wrapper outer
  menuList: 'ul',
  menuListItem: 'li',
  menuTitle: '[data-role="menu-title"]', // .menu__title
  menuTitleText: '[data-role="menu-title-text"]',
  menuItemDecoration: '[data-role="menu-item-decorator"]', // .line
  menuTrigger: '[data-role="menu-trigger"]',
  menuHover: '[data-role="menu-list-hover"]',
  transitionDuration: 450,
};
export default class HeaderNav {
  constructor(props) {
    const options = Object.assign({}, defaults, props);

    this.menuSelector = options.menuSelector;
    this.menu = document.querySelector(options.menuSelector);
    this.menuTrigger = document.querySelectorAll(options.menuTrigger);
    this.menuContainer = this.menu.querySelector(options.menuContainer); // inner
    this.menuWrapperSelector = options.menuWrapper; // outer
    this.menuTitle = this.menu.querySelector(options.menuTitle);
    this.menuTitleText = this.menuTitle.querySelector(options.menuTitleText);
    this.menuScrollElement = options.menuScrollElement;
    this.menuListSelector = options.menuList;
    this.menuList = this.menuContainer.querySelector(options.menuList);
    this.menuListItemSelector = options.menuListItem;
    this.transitionDuration = options.transitionDuration;
    this.menuHover = this.menu.querySelector(options.menuHover);
    this.transitionDelay = 40;

    if (options.menuItemDecoration) {
      this.menuItemDecoration = options.menuItemDecoration;
    }

    // helpers
    this.openedClass = 'menu-opened';
    this.hiddenClass = 'is-hidden';
    this.disabledClass = 'is-disabled';
    this.activeClass = 'is-active';
    this.currentClass = 'is-current';
    this.shownClass = 'is-shown';

    this.menuHoverTextes = [];
    this.imagesLoaded = false;
    this.menuItems = Array.from(this.menuList.children);

    this.init();
  }

  loadImages() {
    if (this.imagesLoaded) return;
    this.menu.querySelectorAll('[data-lazy-loading="true"]').forEach((lazy) => {
      lazy.setAttribute('src', lazy.dataset.src);
      lazy.addEventListener('load', function loaded() {
        this.setAttribute('data-lazy-loaded', true);
      });
    });
    this.imagesLoaded = true;
  }

  toggleStatus() {
    this.loadImages();
    this.scrollToTop();
    DOCUMENT_ELEMENT.classList.toggle(this.openedClass);
    return toggleModal(DOCUMENT_ELEMENT.classList.contains(this.openedClass), this.menuScrollElement, this.menu);
  }

  scrollToTop() {
    return TweenLite.to(
      this.menu,
      this.transitionDuration / 1000,
      { scrollTo: { y: 0, autoKill: false } },
    );
  }

  openInner(e) {
    this.reInit();
    const item = e.target.closest(this.menuListItemSelector) || e.target;
    const list = item.querySelector(this.menuListSelector).cloneNode(true);
    const thisList = Array.from(item.closest(this.menuListSelector).children);
    const newList = Array.from(list.children);
    const listTitle = isMobile()
      ? item.querySelector('span').innerText
      : Array.from(item.querySelector('span').querySelectorAll('[class*="char"]')).map(el => el.innerText || ' ').join('');
    const duration = this.transitionDuration + Math.max(thisList.length, newList.length) * this.transitionDelay;
    this.scrollToTop(true);
    this.addDelayForward(thisList, newList, item);

    dlAnimate.hide(this.menuList, {
      name: 'menu',
      track: 'none',
      duration,
      beforeLeave: () => {
        this.menuTitle.classList.add(this.disabledClass);
        this.menuTitle.classList.remove(this.hiddenClass);
        this.menuTitleText.innerHTML = listTitle;
        dlAnimate.insert(this.menuContainer, list, {
          name: 'menu',
          track: 'none',
          duration,
          beforeEnter: (nextList) => {
            this.refreshList(nextList);
            this.toggleClasses(nextList);
            nextList.setAttribute('data-title', listTitle);
          },
          afterEnter: () => {
            this.menuContainer.classList.add('inner-menu-opened');
            this.menuTitle.classList.remove(this.disabledClass);
          },
        }, this.menuContainer.getElementsByTagName(this.menuListSelector)[0]);
      },
    });
  }

  goBack() {
    if (this.menuTitle.classList.contains(this.disabledClass)) return false;
    this.menuTitle.classList.add(this.disabledClass);
    this.reInit();
    const PREV_LISTS = this.menuContainer.querySelectorAll('.top-list');
    const thisList = Array.from(PREV_LISTS[0].children);
    const newList = Array.from(PREV_LISTS[1].children);
    const duration = this.transitionDuration + Math.max(thisList.length, newList.length) * this.transitionDelay + 20;

    this.addDelayBack(thisList, newList);

    return dlAnimate.remove(PREV_LISTS[0], {
      name: 'menu-back',
      track: 'none',
      duration,
      beforeLeave: () => {
        const $activeText = this.menuHoverTextes.find($el => $el.hasClass(this.currentClass));
        if ($activeText) $activeText.removeClass(this.currentClass);
        setTimeout(() => {
          const $shownText = this.menuHoverTextes.find($el => $el.hasClass(this.shownClass));
          if ($shownText) $shownText.removeClass(this.shownClass);
        }, this.transitionDuration / 1.5);
        dlAnimate.show(PREV_LISTS[1], {
          name: 'menu-back',
          track: 'none',
          duration,
          beforeEnter: (nextList) => {
            this.toggleClasses(nextList);
            const { title } = nextList.dataset;
            if (title) {
              this.menuTitleText.innerHTML = title;
            } else {
              this.menuTitle.classList.add(this.hiddenClass);
            }
          },
          afterEnter: () => {
            this.menuContainer.classList.remove('inner-menu-opened');
            this.menuTitle.classList.remove(this.disabledClass);
          },
        });
      },
      afterLeave: (prevList) => {
        $(prevList).remove();
      },
    });
  }

  toggleClasses(list) {
    Array.from(list.children).forEach((item) => {
      item.classList.add(this.hiddenClass);
      item.addEventListener('animationstart', () => {
        setTimeout(() => {
          item.classList.remove(this.hiddenClass);
        }, 200);
      }, { once: true });
    });
  }

  addDelayBack(thisList, newList) {
    let mult = 1;
    const lengthForward = Math.max(newList.length);
    for (let i = 0; i < lengthForward; i += 1) {
      if (newList[i]) {
        newList[i].style.animationDelay = `${Math.abs(mult * this.transitionDelay)}ms`; // eslint-disable-line no-param-reassign
      }
      mult += 1;
    }
  }

  addDelayForward(thisList, newList, thisItem) {
    const itemIndex = thisList.indexOf(thisItem);

    thisItem.style.animationDelay = '0ms'; // eslint-disable-line no-param-reassign

    let multBack = 1;
    for (let i = itemIndex - 1; i >= 0; i -= 1) {
      thisList[i].style.animationDelay = `${Math.abs(multBack * this.transitionDelay)}ms`; // eslint-disable-line no-param-reassign
      multBack += 1;
    }

    let multForward = 1;
    const lengthForward = Math.max(thisList.length, newList.length);
    for (let i = itemIndex + 1; i < lengthForward; i += 1) {
      thisList[i].style.animationDelay = `${Math.abs(multForward * this.transitionDelay)}ms`; // eslint-disable-line no-param-reassign
      multForward += 1;
    }
  }


  refreshList(list) {
    list.classList.add('top-list');
    Array.from(list.querySelectorAll(this.menuListItemSelector)).forEach((el) => {
      if ($(el).closest(this.menuListSelector).hasClass('top-list')) el.classList.add('top-item');
      if (el.classList.contains('has-children')) {
        $(el).append($(`
          <span class="dots" data-role="dots">
            <span class="dot"></span>
            <span class="dot"></span>
            <span class="dot"></span>
          </span>
        `));
        el.addEventListener('click', (e) => {
          this.openInner(e);
          const index = this.menuItems.indexOf(el);
          this.menuHoverTextes[index].addClass(`${this.currentClass} ${this.shownClass}`);
        });
      }
    });
  }

  initHoverAnimation() {
    this.menuItems.forEach((el, i) => {
      el.setAttribute('data-role', 'double-text-container');
      const $child = $(el).children().first();

      const $doubleText =
          $(`
            <span class="double-text" data-role="sub-text">
              <span data-split-text="letters-only" data-hover-ls="true" class="double-text__inner">${$child.text()}</span>
            </span>
          `);
      const $hoverText =
          $(`
            <div class="menu-hover__text" data-split-text="letters-only">${$child.text()}</div>
          `);
      this.menuHoverTextes.push($hoverText);
      el.addEventListener('mouseenter', () => {
        // jquery instance
        this.menuHoverTextes[i].addClass(this.activeClass);
      });
      el.addEventListener('mouseleave', () => {
        // jquery instance
        this.menuHoverTextes[i].removeClass(this.activeClass);
      });
      if (!isMobile() && !isMicrosoft()) {
        $(el).append($doubleText);
      }
    });
    $(this.menuHover).append(this.menuHoverTextes);
    $(this.menuHover).find('[data-split-text="letters-only"]').each(function trim() {
      const text = $(this).text();
      $(this).text($.trim(text)).lettering();
    });

    if (!isMobile()) {
      $(this.menuScrollElement).find('[data-split-text="letters-only"]').each(function trim() {
        const text = $(this).text();
        $(this).text($.trim(text)).lettering();
      });
      this.menu.querySelectorAll('[data-hover-ls="true"]').forEach(element => new HoverLetterSpacing({ element }));
    }
  }


  init() {
    this.refreshList(this.menuList);
    this.menuTitle.addEventListener('click', (e) => {
      this.goBack(e);
    });


    this.initHoverAnimation();

    document.addEventListener('keyup', (e) => {
      if (e.keyCode === KEY_CODES.ESC && DOCUMENT_ELEMENT.classList.contains(this.openedClass)) {
        this.toggleStatus();
      }
    });

    this.menuTrigger.forEach((trigger) => {
      const text = trigger.querySelector('[data-role="hamburger-text"]');
      if (text) {
        const height = lineHeight(text.querySelector('span'));
        $(text).css({ height, 'line-height': `${height}px` });
      }
      trigger.addEventListener('click', this.toggleStatus.bind(this), false);
    });
    document.addEventListener('click', (e) => {
      if (!e.target.closest(this.menuWrapperSelector)
        && DOCUMENT_ELEMENT.classList.contains(this.openedClass)) {
        this.toggleStatus();
      }
    });
  }

  reInit() {
    this.menuList = this.menuContainer.querySelector(this.menuListSelector);
  }
}



// WEBPACK FOOTER //
// ./scripts/elements/header-nav.js